import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import GuestBanner from '../components/GuestBanner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Palette,
  Feather,
  BookOpen,
  Camera,
  Music,
  Video,
  FileText,
  Package,
  Plus,
  Search,
  Heart,
  Eye,
  DollarSign,
  Download,
  ShoppingCart,
  Loader2,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const contentTypeIcons = {
  art: Palette,
  poetry: Feather,
  story: BookOpen,
  photography: Camera,
  music: Music,
  video: Video,
  guide: FileText,
  other: Package
};

const DigitalContent = () => {
  const { user } = useAuth();
  const [contents, setContents] = useState([]);
  const [myContent, setMyContent] = useState([]);
  const [myPurchases, setMyPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contentTypes, setContentTypes] = useState([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [filters, setFilters] = useState({
    content_type: 'all',
    is_free: 'all',
    search: ''
  });

  const [form, setForm] = useState({
    title: '',
    description: '',
    content_type: 'art',
    price: '',
    is_free: false,
    license_type: 'personal',
    preview_url: '',
    content_url: '',
    file_url: '',
    tags: '',
    pet_related: true
  });

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    try {
      const [contentsRes, typesRes] = await Promise.all([
        axios.get(`${API}/api/content`, {
          params: {
            content_type: filters.content_type !== 'all' ? filters.content_type : undefined,
            is_free: filters.is_free === 'true' ? true : filters.is_free === 'false' ? false : undefined,
            search: filters.search || undefined
          }
        }),
        axios.get(`${API}/api/content/types/list`)
      ]);
      setContents(contentsRes.data);
      setContentTypes(typesRes.data);
      
      if (user) {
        const [myRes, purchasesRes] = await Promise.all([
          axios.get(`${API}/api/content/my-content`),
          axios.get(`${API}/api/content/my-purchases`)
        ]);
        setMyContent(myRes.data);
        setMyPurchases(purchasesRes.data);
      }
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.title || !form.description) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        price: form.is_free ? 0 : parseFloat(form.price) || 0,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean)
      };
      await axios.post(`${API}/api/content`, payload);
      toast.success('Content published!');
      setAddDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish');
    } finally {
      setSaving(false);
    }
  };

  const handlePurchase = async (contentId) => {
    try {
      await axios.post(`${API}/api/content/${contentId}/purchase`);
      toast.success('Purchase successful!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Purchase failed');
    }
  };

  const handleLike = async (contentId) => {
    if (!user) {
      toast.error('Please log in to like content');
      return;
    }
    try {
      await axios.post(`${API}/api/content/${contentId}/like`);
      fetchData();
    } catch (error) {
      toast.error('Failed to like');
    }
  };

  const resetForm = () => {
    setForm({
      title: '',
      description: '',
      content_type: 'art',
      price: '',
      is_free: false,
      license_type: 'personal',
      preview_url: '',
      content_url: '',
      file_url: '',
      tags: '',
      pet_related: true
    });
  };

  const ContentIcon = ({ type }) => {
    const Icon = contentTypeIcons[type] || Package;
    return <Icon className="w-5 h-5" />;
  };

  const ContentCard = ({ content }) => (
    <Card className="card-interactive overflow-hidden" data-testid={`content-${content.id}`}>
      {content.preview_url && (
        <div className="aspect-video bg-muted overflow-hidden">
          <img 
            src={content.preview_url} 
            alt={content.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <ContentIcon type={content.content_type} />
            </div>
            <div>
              <h3 className="font-medium line-clamp-1">{content.title}</h3>
              <p className="text-xs text-muted-foreground capitalize">{content.content_type}</p>
            </div>
          </div>
          {content.is_free ? (
            <Badge variant="secondary">Free</Badge>
          ) : (
            <Badge className="bg-green-100 text-green-700">${content.price}</Badge>
          )}
        </div>
        
        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{content.description}</p>
        
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <Eye className="w-3 h-3" /> {content.view_count}
            </span>
            <span className="flex items-center gap-1">
              <Heart className="w-3 h-3" /> {content.like_count}
            </span>
            <span className="flex items-center gap-1">
              <Download className="w-3 h-3" /> {content.purchase_count}
            </span>
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Avatar className="w-6 h-6">
              <AvatarImage src={content.creator_avatar} />
              <AvatarFallback>{content.creator_username?.charAt(0)}</AvatarFallback>
            </Avatar>
            <span className="text-sm">{content.creator_username}</span>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="icon" onClick={() => handleLike(content.id)}>
              <Heart className="w-4 h-4" />
            </Button>
            {user && !content.is_purchased && !content.is_free && (
              <Button size="sm" onClick={() => handlePurchase(content.id)}>
                <ShoppingCart className="w-4 h-4 mr-1" />
                Buy
              </Button>
            )}
            {(content.is_purchased || content.is_free) && content.file_url && (
              <Button size="sm" variant="outline" asChild>
                <a href={content.file_url} target="_blank" rel="noopener noreferrer">
                  <Download className="w-4 h-4 mr-1" />
                  Download
                </a>
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <GuestBanner message="Sign up to sell your pet-related art, stories, and more!" />
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-heading font-medium flex items-center gap-3">
              <Palette className="w-8 h-8 text-primary" />
              Creative Marketplace
            </h1>
            <p className="text-muted-foreground">
              Buy and sell pet-related art, stories, and digital content
            </p>
          </div>
          {user && (
            <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-content-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Publish Content
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Publish Digital Content</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <Input
                    placeholder="Title *"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    data-testid="content-title"
                  />
                  <Textarea
                    placeholder="Description *"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows={3}
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <Select value={form.content_type} onValueChange={(val) => setForm({ ...form, content_type: val })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Type" />
                      </SelectTrigger>
                      <SelectContent>
                        {contentTypes.map((type) => (
                          <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={form.license_type} onValueChange={(val) => setForm({ ...form, license_type: val })}>
                      <SelectTrigger>
                        <SelectValue placeholder="License" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="personal">Personal Use</SelectItem>
                        <SelectItem value="commercial">Commercial</SelectItem>
                        <SelectItem value="exclusive">Exclusive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={form.is_free}
                        onChange={(e) => setForm({ ...form, is_free: e.target.checked })}
                      />
                      <span className="text-sm">Free content</span>
                    </label>
                    {!form.is_free && (
                      <Input
                        placeholder="Price ($)"
                        type="number"
                        value={form.price}
                        onChange={(e) => setForm({ ...form, price: e.target.value })}
                        className="w-32"
                      />
                    )}
                  </div>
                  <Input
                    placeholder="Preview Image URL"
                    value={form.preview_url}
                    onChange={(e) => setForm({ ...form, preview_url: e.target.value })}
                  />
                  <Input
                    placeholder="Download File URL"
                    value={form.file_url}
                    onChange={(e) => setForm({ ...form, file_url: e.target.value })}
                  />
                  <Input
                    placeholder="Tags (comma-separated)"
                    value={form.tags}
                    onChange={(e) => setForm({ ...form, tags: e.target.value })}
                  />
                  <Button onClick={handleCreate} className="w-full" disabled={saving}>
                    {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Publish
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search content..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="pl-10"
            />
          </div>
          <Select value={filters.content_type} onValueChange={(val) => setFilters({ ...filters, content_type: val })}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {contentTypes.map((type) => (
                <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.is_free} onValueChange={(val) => setFilters({ ...filters, is_free: val })}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="All Prices" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Prices</SelectItem>
              <SelectItem value="true">Free Only</SelectItem>
              <SelectItem value="false">Paid Only</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="browse" className="space-y-6">
          <TabsList>
            <TabsTrigger value="browse">Browse All</TabsTrigger>
            {user && <TabsTrigger value="my-content">My Content ({myContent.length})</TabsTrigger>}
            {user && <TabsTrigger value="purchases">Purchases ({myPurchases.length})</TabsTrigger>}
          </TabsList>

          <TabsContent value="browse">
            {contents.length === 0 ? (
              <Card className="text-center py-16">
                <Palette className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                <h3 className="font-heading text-xl mb-2">No content yet</h3>
                <p className="text-muted-foreground">Be the first to publish!</p>
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {contents.map((content) => (
                  <ContentCard key={content.id} content={content} />
                ))}
              </div>
            )}
          </TabsContent>

          {user && (
            <>
              <TabsContent value="my-content">
                {myContent.length === 0 ? (
                  <Card className="text-center py-16">
                    <Package className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                    <h3 className="font-heading text-xl mb-2">No content published</h3>
                    <p className="text-muted-foreground">Click "Publish Content" to get started</p>
                  </Card>
                ) : (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {myContent.map((content) => (
                      <ContentCard key={content.id} content={content} />
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="purchases">
                {myPurchases.length === 0 ? (
                  <Card className="text-center py-16">
                    <ShoppingCart className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                    <h3 className="font-heading text-xl mb-2">No purchases yet</h3>
                    <p className="text-muted-foreground">Browse and support creators!</p>
                  </Card>
                ) : (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {myPurchases.map((content) => (
                      <ContentCard key={content.id} content={content} />
                    ))}
                  </div>
                )}
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default DigitalContent;
